import type { ReactNode } from 'react';
import clsx from 'clsx';
import { InfoTip } from './InfoTip';

/**
 * The standard page container: centred, max-width, consistent padding and
 * vertical rhythm. Pages previously each chose their own combination of
 * `max-w-7xl px-6 py-8` / `p-8` / `space-y-6` / `space-y-8`.
 */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('mx-auto max-w-7xl px-6 py-8 space-y-6', className)}>{children}</div>;
}

interface PageHeaderProps {
  title: string;
  /** The small Space Mono line above the title — the section this page sits in. */
  eyebrow?: string;
  /** One plain sentence. Method and source explanations go in `info`, not here. */
  subtitle?: string;
  /** Rendered in an (i) popover next to the title — the "how this is computed" text. */
  info?: ReactNode;
  /** The grey line under the subtitle: sync time, source, data notes chip. */
  meta?: ReactNode;
  /** Rendered top-right — a range selector, a refresh button, a primary action. */
  actions?: ReactNode;
  /** Rendered below the title block, typically a <TabBar />. */
  children?: ReactNode;
}

/** One page-title treatment for every page. */
export function PageHeader({ title, eyebrow, subtitle, info, meta, actions, children }: PageHeaderProps) {
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
          <div className="flex items-center gap-2">
            <h1 className="text-page-title text-text-primary">{title}</h1>
            {info && <InfoTip>{info}</InfoTip>}
          </div>
          {subtitle && <p className="text-body-sm text-text-secondary mt-1">{subtitle}</p>}
          {meta && <div className="mt-1 flex flex-wrap items-center gap-2 text-body-xs text-text-tertiary">{meta}</div>}
        </div>
        {actions && <div className="flex-none flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/** A quiet section heading for grouping panels within a page. */
export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="eyebrow">{children}</h2>
      {action}
    </div>
  );
}
